// frontend/src/features/agency/ReservedDriverList.js
// 
import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  upsertDriver,
  upsertDrivers,
  removeDriver,
  selectDriversByStatus,
  applyStatusChange, // ✅ thunk from driverSlice
} from '../../features/drivers/driverSlice';
import { fetchDrivers, addDriver } from '../../services/driverService';
import axios from 'axios';
import socket from '../../services/socket';
import DriverForm from '../../features/drivers/DriverForm';
import DriverEditModal from '../../features/drivers/DriverEditModal';
import { RefreshCcw, Trash2, Edit2 } from 'lucide-react';
import { selectAgencyIdForUser } from '../auth/authSlice';

export default function ReservedDriverList() {
  const dispatch = useDispatch();
  const agencyId = useSelector(selectAgencyIdForUser);
  const drivers  = useSelector(selectDriversByStatus('Reserved'));

  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);

  // Initial fetch (non-destructive; correct call shape)
  useEffect(() => {
    (async () => {
      try {
        const data = await fetchDrivers({ status: 'Reserved' });
        dispatch(upsertDrivers(data || []));
      } catch (e) {
        console.error('[ReservedDriverList] fetch failed', e);
      }
    })();
  }, [dispatch]);

  // Real-time updates (always upsert on add/update; patch status on ban/unban; remove only on delete)
  useEffect(() => {
    const sameAgency = d =>
      agencyId == null || Number(d?.agency_id) === Number(agencyId);

    const handleAdded = d => {
      if (sameAgency(d)) dispatch(upsertDriver(d));
    };

    const handleUpdated = d => {
      if (sameAgency(d)) dispatch(upsertDriver(d));
    };

    const handleDeleted = ({ id }) => {
      dispatch(removeDriver(id)); // only real deletions
    };

    const handleBanned = payload => {
      const id = payload?.id ?? payload?.driver_id;
      if (!id) return;
      dispatch(applyStatusChange({ id, status: 'Banned' })); // status flip
    };

    const handleUnbanned = payload => {
      const id = payload?.id ?? payload?.driver_id;
      if (!id) return;
      // If your flow returns to 'Active' after unban, change here
      dispatch(applyStatusChange({ id, status: 'Reserved' }));
    };

    socket.on('driverAdded',    handleAdded);
    socket.on('driverUpdated',  handleUpdated);
    socket.on('driverDeleted',  handleDeleted);
    socket.on('driverBanned',   handleBanned);
    socket.on('driverUnbanned', handleUnbanned);

    return () => {
      socket.off('driverAdded',    handleAdded);
      socket.off('driverUpdated',  handleUpdated);
      socket.off('driverDeleted',  handleDeleted);
      socket.off('driverBanned',   handleBanned);
      socket.off('driverUnbanned', handleUnbanned);
    };
  }, [dispatch, agencyId]);

  // Reserve (create)
  const handleCreate = async formData => {
    try {
      const newDrv = await addDriver({ ...formData, status: 'Reserved' });
      dispatch(upsertDriver(newDrv));
      setShowAddModal(false);
    } catch (err) {
      console.error('Error reserving driver:', err);
    }
  };

  // Activate a reserved driver (status flip → upsert, NOT remove)
  const handleActivate = async id => {
    try {
      const res = await axios.put(`/agencies/drivers/${id}`, { status: 'Active' });
      const updated = res.data || res;
      dispatch(upsertDriver(updated));
      // Alternatively, if API doesn't return the full record:
      // dispatch(applyStatusChange({ id, status: 'Active' }));
    } catch (err) {
      console.error('Error activating driver:', err);
    }
  };

  // Delete reserved driver (real deletion → remove)
  const handleDeleteClick = async id => {
    try {
      await axios.delete(`/agencies/drivers/${id}`); // unified path
      dispatch(removeDriver(id));
    } catch (err) {
      console.error('Error deleting driver:', err);
    }
  };

  return (
    <>
      <div className="flex justify-end mb-4">
        <button
          onClick={() => setShowAddModal(true)}
          className="px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600 transition"
        >
          Reserve Driver
        </button>
      </div>

      {showAddModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowAddModal(false)}
        >
          <div onClick={e => e.stopPropagation()}>
            <DriverForm
              initialStatus="Reserved"
              onSave={handleCreate}
              onClose={() => setShowAddModal(false)}
            />
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full bg-white rounded-lg shadow overflow-hidden">
          <thead className="bg-gray-50">
            <tr>
              {['Name','Vehicle Reg.','Status','Actions'].map(col => (
                <th
                  key={col}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {drivers.map(driver => (
              <tr key={driver.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {driver.name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {driver.vehicle_registration_number}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {driver.status}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={() => setSelectedDriver(driver)}
                      className="p-1 hover:bg-gray-100 rounded"
                      title="Edit"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleActivate(driver.id)}
                      className="p-1 hover:bg-gray-100 rounded text-green-600"
                      title="Activate"
                    >
                      <RefreshCcw size={16} />
                    </button>
                    <button
                      onClick={() => handleDeleteClick(driver.id)}
                      className="p-1 hover:bg-gray-100 rounded text-red-600"
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedDriver && (
        <DriverEditModal
          driver={selectedDriver}
          onClose={() => setSelectedDriver(null)}
          onUpdate={() => setSelectedDriver(null)}
        />
      )}
    </>
  );
}
